import React, { useState, useEffect, useCallback } from 'react';
import '../App.css';
import Swal from 'sweetalert2';
import axiosInstance from '../api/axiosInstance';
import MultiSelectSupervisor from './MultiSelectSupervisor'; // MultiSelectSupervisor 임포트 추가
import TaskDetailModal from './TaskDetailModal'; // TaskDetailModal 임포트 추가

function AssignTaskToDateModal({ isOpen, onClose, selectedDates, supervisors, onAssignTask, assignedEntries }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedExistingTask, setSelectedExistingTask] = useState(null);
  const [assignedManagers, setAssignedManagers] = useState([]);
  const [assignedAssistants, setAssignedAssistants] = useState([]);
  const [projectType, setProjectType] = useState('재착공'); // Default to '재착공'
  const [activeTab, setActiveTab] = useState('단가');
  const [selectedYear, setSelectedYear] = useState('all');
  const [allTasks, setAllTasks] = useState([]);
  const [specialNotesForSelectedDates, setSpecialNotesForSelectedDates] = useState([]);
  const [isTaskDetailModalOpen, setIsTaskDetailModalOpen] = useState(false); // State for TaskDetailModal
  const [taskToView, setTaskToView] = useState(null); // State to hold task data for TaskDetailModal

  const currentYear = new Date().getFullYear();
  const years = ['all', ...Array.from({ length: 11 }, (_, i) => (currentYear - 5 + i).toString())];

  const fetchAllTasks = useCallback(async (year) => {
    try {
      const url = year === 'all' ? '/tasks' : `/tasks?workYear=${year}`; // Changed 'year' to 'workYear'
      const response = await axiosInstance.get(url);
      setAllTasks(response.data);
    } catch (error) {
      console.error('Error fetching all tasks for modal:', error);
    }
  }, [setAllTasks]);

  const fetchSpecialNotesForSelectedDates = useCallback(async (dates) => {
    if (dates.length === 0) {
      setSpecialNotesForSelectedDates([]);
      return;
    }
    try {
      const dateParams = dates.map(date => `dates=${date}`).join('&');
      const response = await axiosInstance.get(`/special-notes?${dateParams}`);
      setSpecialNotesForSelectedDates(response.data);
    } catch (error) {
      console.error('Error fetching special notes for selected dates:', error);
    }
  }, [setSpecialNotesForSelectedDates]);

  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      setSelectedExistingTask(null);
      setAssignedManagers([]);
      setAssignedAssistants([]);
      setActiveTab('단가');
      fetchSpecialNotesForSelectedDates(selectedDates);
    }
  }, [isOpen, selectedDates, fetchSpecialNotesForSelectedDates]);

  useEffect(() => {
    if (isOpen) {
      if (activeTab === '단가') {
        setSelectedYear(currentYear.toString());
      } else if (activeTab === '총가') {
        setSelectedYear('all');
      }
    }
  }, [isOpen, activeTab, currentYear]);

  useEffect(() => {
    if (isOpen) {
      fetchAllTasks(selectedYear);
    }
  }, [selectedYear, isOpen, fetchAllTasks]);

  const getFilteredTasks = () => {
    return allTasks.filter(task => {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      const matchesSearch = (
        task.name.toLowerCase().includes(lowerCaseSearchTerm) ||
        task.displayNumber.toLowerCase().includes(lowerCaseSearchTerm) ||
        task.number.toLowerCase().includes(lowerCaseSearchTerm) ||
        (task.mainTaskNumber || '').toLowerCase().includes(lowerCaseSearchTerm) 
      );
      const matchesTab = task.type === activeTab;

      return matchesSearch && matchesTab;
    }).sort((a, b) => {
      const numA = parseInt(a.displayNumber, 10);
      const numB = parseInt(b.displayNumber, 10);
      return numA - numB;
    });
  };

  if (!isOpen) return null;

  const handleAssignExistingTask = () => {
    if (!selectedExistingTask) {
      Swal.fire({
        icon: 'warning',
        title: '선택 오류',
        text: '기존 공사를 선택해주세요.',
      });
      return;
    }
    if (assignedManagers.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: '선택 오류',
        text: '책임감리원을 한 명 이상 선택해주세요.',
      });
      return;
    }
    // onAssignTask 호출 시 deploymentStatus 추가
    onAssignTask(selectedExistingTask.id, assignedManagers, assignedAssistants, '배치계획', projectType);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content assign-modal-content" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="modal-close-button">×</button>
        <h2>{selectedDates.length > 1 ? `${selectedDates[0]} ~ ${selectedDates[selectedDates.length - 1]}에 공사 할당` : `${selectedDates[0]}에 공사 할당`}</h2>

        <div className="assign-task-section">
          <div className="filter-controls">
            <select onChange={(e) => setSelectedYear(e.target.value)} value={selectedYear} className="year-select">
              <option value="all">전체공사목록</option>
              {years.filter(year => year !== 'all').map(year => (
                <option key={year} value={year}>{year}년</option>
              ))}
            </select>
            <input
              type="text"
              placeholder="공사명, 번호, 공사번호로 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input full-width-input"
            />
          </div>
          <div className="tabs">
            <button
              className={`tab-button ${activeTab === '단가' ? 'active' : ''}`}
              onClick={() => setActiveTab('단가')}
            >
              단가
            </button>
            <button
              className={`tab-button ${activeTab === '총가' ? 'active' : ''}`}
              onClick={() => setActiveTab('총가')}
            >
              총가
            </button>
          </div>
          {selectedExistingTask && (
            <>
              <div className="selected-task-info" style={{ position: 'relative', paddingBottom: '70px' }}>
              {/* <p>선택된 공사: <strong>[{selectedExistingTask.displayNumber}] {selectedExistingTask.name}</strong></p> */}
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', width: '100%' }}>
                {/* Left Column */}
                <div style={{ flex: 1 }}>
                  <div className="form-group">
                    <label>공사명</label>
                    <input type="text" value={selectedExistingTask.name} readOnly={true} />
                  </div>
                  <div className="form-group">
                    <label>공사번호(감리)</label>
                    <input type="text" value={selectedExistingTask.number} readOnly={true} />
                    <button 
                      onClick={() => {
                        setTaskToView(selectedExistingTask);
                        setIsTaskDetailModalOpen(true);
                      }} 
                      className="action-button" 
                      style={{ marginTop: '30px', width: '50%' }}
                    >
                      공사 정보 보기
                    </button>
                  </div>
                </div>

                {/* Right Column */}
                <div style={{ flex: 1 }}>
                  <div className="form-group">
                    <label>배정 책임감리</label>
                    <MultiSelectSupervisor
                      supervisors={supervisors}
                      selectedSupervisors={assignedManagers}
                      onSelectionChange={setAssignedManagers}
                      placeholder="책임감리원을 선택하세요"
                      isManager={true}
                      otherSelectedSupervisors={assignedAssistants}
                      duplicateCheckEntries={assignedEntries}
                      specialNotes={specialNotesForSelectedDates}
                    />
                  </div>
                  <div className="form-group">
                    <label>배정 보조감리</label>
                    <MultiSelectSupervisor
                      supervisors={supervisors}
                      selectedSupervisors={assignedAssistants}
                      onSelectionChange={setAssignedAssistants}
                      placeholder="보조감리원을 선택하세요"
                      isManager={false}
                      otherSelectedSupervisors={assignedManagers}
                      duplicateCheckEntries={assignedEntries}
                      specialNotes={specialNotesForSelectedDates}
                    />
                  </div>
                  <div className="form-group">
                    <label>공사 현황</label>
                    <select
                      value={projectType}
                      onChange={(e) => setProjectType(e.target.value)}
                      className="form-control"
                    >
                      <option value="재착공">재착공</option>
                      <option value="준공">준공</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={{ position: 'absolute', bottom: '20px', right: '20px' }}>
                <button onClick={handleAssignExistingTask} className="submit-button">감리원 배치</button>
              </div>
            </div>
            </>
          )}
          
          <div className="task-list-container">
            {getFilteredTasks().length > 0 ? (
              <table className="main-table assign-task-table">
                <thead>
                  <tr>
                    <th>계약연도</th>
                    <th>번호</th>
                    <th>공사명</th>
                    <th>공사번호(감리)</th>
                    <th>책임감리</th>
                    <th>보조감리</th>
                  </tr>
                </thead>
                <tbody>
                  {getFilteredTasks().map(task => {
                    const isCompleted = task.taskStatus === '준공';
                    return (
                    <tr
                      key={task.id}
                      onClick={() => !isCompleted && setSelectedExistingTask(task)}
                      className={`${selectedExistingTask && selectedExistingTask.id === task.id ? 'selected' : ''} ${isCompleted ? 'completed-task' : ''}`}
                      style={{ cursor: isCompleted ? 'not-allowed' : 'pointer' }}
                    >
                      <td>{new Date(task.contractDate).getFullYear()}</td>
                      <td>{task.displayNumber}</td>
                      <td>{task.name}{isCompleted ? ' (준공)' : ''}</td>
                      <td>{task.number}</td>
                      <td>{task.managers}</td>
                      <td>{task.assistants}</td>
                    </tr>
                    )
                  })}
                </tbody>
              </table>
            ) : (
              <p>표시할 공사가 없습니다.</p>
            )}
          </div>
        </div>
      </div>
      {taskToView && (
        <TaskDetailModal
          isOpen={isTaskDetailModalOpen}
          onClose={() => setIsTaskDetailModalOpen(false)}
          task={taskToView}
          // Pass dummy functions for onUpdateTask and onDeleteTask as they are not needed here
          onUpdateTask={() => {}}
          onDeleteTask={() => {}}
          isEditMode={false} // Always open in view mode
          supervisors={supervisors} // Pass supervisors prop
          contractors={[]} // Pass empty contractors array or fetch if needed
        />
      )}
    </div>
  );
}

export default AssignTaskToDateModal;